import { observer } from 'mobx-react-lite'
import { ChatBubbleEmpty, EditPencil, Trash } from 'iconoir-react'
import { useMemo } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import moment from 'moment'
import { Button } from 'app/components/ui/button'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeader } from 'app/components/ui/page-header'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import SimpleTable from 'app/components/table/SimpleTable'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { useTemplateTabs } from 'app/components/templates/useTemplateTabs'
import type { SmsTemplate } from 'app/generated/graphql'
import { writeFallbacks } from 'app/lib/workflows/messageFallbacks'
import { TemplateEditorDialog } from './TemplateEditorDialog'
import type { UseSmsTemplatesReturn } from './useSmsTemplates'

export const SmsTemplatesShadcnComponent = observer(function SmsTemplatesShadcnComponent({
  templates,
  saving,
  setSearch,
  editing,
  openNew,
  openEdit,
  closeEditor,
  saveTemplate,
  removeTemplate,
}: UseSmsTemplatesReturn) {
  const { tabs } = useTemplateTabs()
  const columns = useMemo<ColumnDef<SmsTemplate>[]>(
    () => [
      {
        id: `name`,
        accessorKey: `name`,
        header: `Name`,
        cell: (props) => <span className="font-medium">{props.getValue<string>()}</span>,
        meta: { defaultVisible: true },
      },
      {
        id: `body`,
        accessorKey: `body`,
        header: `Message`,
        cell: (props) => (
          <span className="line-clamp-2 max-w-xl text-subtle">{writeFallbacks(props.getValue<string>(), {})}</span>
        ),
        meta: { defaultVisible: true },
      },
      {
        id: `updated`,
        accessorKey: `updatedDate`,
        header: `Updated`,
        cell: (props) => moment(props.getValue<string>()).format(`MMM D, YYYY`),
        meta: { defaultVisible: true },
      },
      {
        id: `actions`,
        header: ``,
        meta: { defaultVisible: true },
        cell: (props) => (
          // stopPropagation: the row itself opens the editor, so a click here
          // must not also fire that (it would reopen the editor behind a
          // delete, or open it twice).
          <div className="flex items-center gap-3">
            <TooltipWrapper message="Edit template">
              <EditPencil
                className="h-4 w-4 cursor-pointer text-subtle hover:text-default"
                onClick={(e) => {
                  e.stopPropagation()
                  openEdit(props.row.original)
                }}
              />
            </TooltipWrapper>
            <TooltipWrapper message="Delete template">
              <Trash
                className="h-4 w-4 cursor-pointer text-red-500 hover:text-red-700"
                onClick={(e) => {
                  e.stopPropagation()
                  removeTemplate(props.row.original)
                }}
              />
            </TooltipWrapper>
          </div>
        ),
      },
    ],
    [openEdit, removeTemplate],
  )

  return (
    <>
      <PageScroll variant="table" header={<PageHeader title="Templates" tabs={tabs} />}>
        {/* pt-6 = the house gap between a tab underline and the toolbar row
            (matches Mapping's tab children). */}
        <div className="h-full pt-6">
          <SimpleTable
            columns={columns}
            data={templates}
            hideFooter
            // Opening a template is the primary action, so the whole row is the
            // target. The Edit pencil is the last column of a wide table; on a
            // phone it sits behind a long horizontal scroll.
            meta={{ onRowClick: openEdit }}
            emptyState={
              <TableEmptyState
                icon={<ChatBubbleEmpty className="h-6 w-6" strokeWidth={1.5} />}
                title="No SMS templates yet"
                description="Save the texts your team sends over and over, like running late, recall reminders, and insurance checks. Personalize them with contact details, then reuse them from the inbox, bulk send, and workflows."
                action={
                  <Button variant="primary" size="sm" onClick={openNew}>
                    New Template
                  </Button>
                }
              />
            }
            edgePadded
            onSearchValueChange={setSearch}
            // Tabbed page: the primary action sits beside search, not in the
            // header, same layout as Mapping's tab children.
            extraSlot={
              <Button variant="primary" size="sm" onClick={openNew}>
                New Template
              </Button>
            }
          />
        </div>
      </PageScroll>
      <TemplateEditorDialog
        open={!!editing}
        template={editing === `new` ? undefined : editing}
        saving={saving}
        onSave={saveTemplate}
        onCancel={closeEditor}
      />
    </>
  )
})
